import React from 'react';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import Birthday from '../../../assets/images/birthday.svg';
import BonusEvent from '../../../assets/images/bonus-event.svg';
import Challenge from '../../../assets/images/challenge.svg';
import Orders from '../../../assets/images/orders.svg';
import Star from '../../../assets/images/star_filled.svg';
import StoreEvent from '../../../assets/images/store-event.svg';

const Dot = () => (
    <span className={dt(['inline-block', 'w-3', 'h-3', 'bg-black', 'rounded-full'])} />
);

const rowClasses = dt([
    'h-[60px]',
    'flex',
    'items-center',
    'justify-center',
    'text-center',
    'text-small',
    'smOnly:text-xsmall',
    'px-1',
]);
const labelRowClasses = dt(['h-[60px]', 'flex', 'items-center', 'gap-2', 'pr-2']);
const tierColumnClasses = (isActive) =>
    `${dt([
        'flex',
        'flex-col',
        'flex-1',
        isActive ? 'border-2' : 'border-0',
        'border-solid',
        'rounded-md',
        isActive ? 'border-brand-red' : '',
        'relative',
        'overflow-hidden',
        'bg-[#FAFAFA]',
        'smOnly:min-w-[130px]',
    ])}`;
const tierHeaderClasses = (isActive) =>
    dt([
        'h-[60px]',
        'flex',
        'items-center',
        'justify-center',
        'font-open-sans-semibold',
        'text-normal',
        'smOnly:text-small',
        'border-b-4',
        'border-solid',
        isActive ? 'border-black' : 'border-gray-60',
    ]);

const TierBenefits = ({ currentTier = 'MEMBER' }) => {
    const isActive = (tier) => tier === currentTier.toUpperCase();

    return (
        <div className={dt(['w-full', 'float-left', 'mt-6', 'mb-4'])}>
            <h2 className={dt(['text-xlarge', 'font-open-sans-bold', 'mb-4'])}>Tier Benefits</h2>
            <div className={dt(['flex', 'flex-row', 'w-full'])}>
                {/* Left labels column */}
                <div
                    className={dt([
                        'flex',
                        'flex-col',
                        'min-w-[160px]',
                        'smOnly:min-w-[120px]',
                        'smOnly:max-w-[220px]',
                    ])}
                >
                    <div className={dt(['h-[60px]', 'flex', 'items-center'])}>
                        <span className={dt(['font-open-sans-semibold', 'text-normal'])}>
                            Tier Level
                        </span>
                    </div>
                    <div className={labelRowClasses}>
                        <span
                            className={dt([
                                'text-small',
                                'smOnly:text-xsmall',
                                'font-medium',
                                'text-black',
                            ])}
                        >
                            Points per $ spent
                        </span>
                    </div>
                    <div className={labelRowClasses}>
                        <Icon iconType="svg" width="24px" height="24px" viewBox="0 0 16 16">
                            <BonusEvent />
                        </Icon>
                        <span className={dt(['text-small', 'smOnly:text-xsmall'])}>
                            Bonus Events
                        </span>
                    </div>
                    <div className={labelRowClasses}>
                        <Icon iconType="svg" width="28px" height="28px" viewBox="0 0 16 16">
                            <Challenge />
                        </Icon>
                        <span className={dt(['text-small', 'smOnly:text-xsmall'])}>
                            Exclusive Challenges
                        </span>
                    </div>
                    <div className={`${labelRowClasses} ${dt(['ml-[-6px]'])}`}>
                        <Icon iconType="svg" width="40px" height="40px" viewBox="0 0 16 16">
                            <Birthday />
                        </Icon>
                        <span className={dt(['text-small', 'smOnly:text-xsmall'])}>
                            Birthday Gifts
                        </span>
                    </div>
                    <div className={`${labelRowClasses} ${dt(['ml-[-6px]'])}`}>
                        <Icon iconType="svg" width="42px" height="42px" viewBox="0 0 16 16">
                            <StoreEvent />
                        </Icon>
                        <span className={dt(['text-small', 'smOnly:text-xsmall'])}>
                            Exclusive In-Store Events
                        </span>
                    </div>
                    <div className={`${labelRowClasses} ${dt(['ml-[-1px]'])}`}>
                        <Icon iconType="svg" width="34px" height="34px" viewBox="0 0 16 16">
                            <Star />
                        </Icon>
                        <span className={dt(['text-small', 'smOnly:text-xsmall'])}>
                            One Complimentary Service Annually
                        </span>
                    </div>
                    <div className={`${labelRowClasses} ${dt(['ml-[-2px]'])}`}>
                        <Icon iconType="svg" width="40px" height="40px" viewBox="0 0 16 16">
                            <Orders />
                        </Icon>
                        <span className={dt(['text-small', 'smOnly:text-xsmall'])}>
                            Free Shipping
                        </span>
                    </div>
                </div>

                {/* Tier columns */}
                <div
                    className={dt([
                        'flex',
                        'flex-row',
                        'flex-1',
                        'gap-2',
                        'smOnly:gap-1',
                        'mt-[-18px]',
                        'smOnly:overflow-x-auto',
                        'smOnly:flex-nowrap',
                    ])}
                >
                    {/* MEMBER */}
                    <div className={tierColumnClasses(isActive('MEMBER'))}>
                        <div
                            className={dt([
                                isActive('MEMBER') ? 'bg-brand-red' : 'bg-white',
                                'text-white',
                                'text-center',
                                'text-xsmall',
                                'py-0.5',
                                'font-semibold',
                            ])}
                        >
                            {isActive('MEMBER') ? 'YOUR TIER' : '\u00A0'}
                        </div>
                        <div className={tierHeaderClasses(isActive('MEMBER'))}>MEMBER</div>
                        <div className={rowClasses}>1 Point</div>
                        <div className={rowClasses}>
                            <Dot />
                        </div>
                        <div className={rowClasses} />
                        <div className={rowClasses} />
                        <div className={rowClasses} />
                        <div className={rowClasses} />
                        <div className={rowClasses} />
                    </div>

                    {/* INSIDER */}
                    <div className={tierColumnClasses(isActive('INSIDER'))}>
                        <div
                            className={dt([
                                isActive('INSIDER') ? 'bg-brand-red' : 'bg-white',
                                'text-white',
                                'text-center',
                                'text-xsmall',
                                'py-0.5',
                                'font-semibold',
                            ])}
                        >
                            {isActive('INSIDER') ? 'YOUR TIER' : '\u00A0'}
                        </div>
                        <div className={tierHeaderClasses(isActive('INSIDER'))}>INSIDER</div>
                        <div className={rowClasses}>1.5 with JCP Card</div>
                        <div className={rowClasses}>
                            <Dot />
                        </div>
                        <div className={rowClasses}>2 per year</div>
                        <div className={rowClasses}>
                            <Dot />
                        </div>
                        <div className={rowClasses}>
                            <Dot />
                        </div>
                        <div className={rowClasses} />
                        <div className={rowClasses} />
                    </div>

                    {/* ELITE */}
                    <div className={tierColumnClasses(isActive('ELITE'))}>
                        <div
                            className={dt([
                                isActive('ELITE') ? 'bg-brand-red' : 'bg-white',
                                'text-white',
                                'text-center',
                                'text-xsmall',
                                'py-0.5',
                                'font-semibold',
                            ])}
                        >
                            {isActive('ELITE') ? 'YOUR TIER' : '\u00A0'}
                        </div>
                        <div className={tierHeaderClasses(isActive('ELITE'))}>ELITE</div>
                        <div className={rowClasses}>1.5 with JCP Card</div>
                        <div className={rowClasses}>
                            <Dot />
                        </div>
                        <div className={rowClasses}>4 per year</div>
                        <div className={rowClasses}>
                            <Dot />
                        </div>
                        <div className={rowClasses}>
                            <Dot />
                        </div>
                        <div className={rowClasses} />
                        <div className={rowClasses} />
                    </div>

                    {/* VIP */}
                    <div className={tierColumnClasses(isActive('VIP'))}>
                        <div
                            className={dt([
                                isActive('VIP') ? 'bg-brand-red' : 'bg-white',
                                'text-white',
                                'text-center',
                                'text-xsmall',
                                'py-0.5',
                                'font-semibold',
                            ])}
                        >
                            {isActive('VIP') ? 'YOUR TIER' : '\u00A0'}
                        </div>
                        <div className={tierHeaderClasses(isActive('VIP'))}>VIP</div>
                        <div className={rowClasses}>2 with JCP Card</div>
                        <div className={rowClasses}>
                            <Dot />
                        </div>
                        <div className={rowClasses}>6 per year</div>
                        <div className={rowClasses}>
                            <Dot />
                        </div>
                        <div className={rowClasses}>
                            <Dot />
                        </div>
                        <div className={rowClasses}>
                            <Dot />
                        </div>
                        <div className={rowClasses}>
                            <span style={{ whiteSpace: 'pre-line' }}>
                                {'Ship To Home\non orders over $25'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TierBenefits;
